import { useCallback, useEffect, useState } from "react";
import { deleteAdminReview, restoreAdminReview, searchAdminReviews } from "@/api/admin.api.js";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import { getFileUrl } from "@/api/public.api.js";
import "@/pages/product/product-reviews.css";

function formatReviewDate(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("fr-FR");
}

export default function AdminReviewsSection({ setMessage, setError }) {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("ACTIVE");
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [pendingRestore, setPendingRestore] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await searchAdminReviews({
                search: search.trim(),
                status: status || undefined,
                page: 0,
                size: 50
            });
            setReviews(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [search, status, setError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = async () => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteAdminReview(pendingDelete.id);
            setMessage("Avis masqué (conservé en base).");
            setPendingDelete(null);
            await load();
        } catch (err) {
            setError(err.message);
            setPendingDelete(null);
        }
    };

    const handleRestore = async () => {
        if (!pendingRestore) {
            return;
        }
        try {
            await restoreAdminReview(pendingRestore.id);
            setMessage("Avis de nouveau visible sur la fiche produit.");
            setPendingRestore(null);
            await load();
        } catch (err) {
            setError(err.message);
            setPendingRestore(null);
        }
    };

    return (
        <section className="card profile-section profile-section--wide">
            <h2>Gérer les avis</h2>
            <p className="muted admin-list-hint">
                Recherchez les avis produits. Les avis anonymes restent visibles avec le client ici.
            </p>
            <form
                className="profile-form-actions"
                onSubmit={(event) => {
                    event.preventDefault();
                    load();
                }}
            >
                <label className="field" style={{ flex: 1, minWidth: "12rem" }}>
                    Recherche
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Produit, client, commentaire…"
                    />
                </label>
                <label className="field">
                    Statut
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">Tous</option>
                        <option value="ACTIVE">Actifs</option>
                        <option value="DELETED">Masqués</option>
                    </select>
                </label>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Chargement…" : "Filtrer"}
                </button>
            </form>

            {loading && <p className="muted">Chargement des avis...</p>}
            {!loading && reviews.length === 0 && (
                <p className="muted">Aucun avis ne correspond à votre recherche.</p>
            )}

            <div className="admin-table purchase-list" style={{ marginTop: "1rem" }}>
                {reviews.map((review) => (
                    <div key={review.id} className="admin-row admin-list-row purchase-list-row">
                        <div className="admin-list-row-main purchase-list-row-main purchase-list-row-main--static">
                            <div className="purchase-list-row-top">
                                {review.authorPhotoId ? (
                                    <img
                                        src={getFileUrl(review.authorPhotoId)}
                                        alt=""
                                        className="purchase-row-thumb"
                                    />
                                ) : (
                                    <div className="purchase-row-thumb purchase-row-thumb--empty" aria-hidden="true" />
                                )}
                                <div className="purchase-list-row-heading">
                                    <strong>{review.productTitle}</strong>
                                </div>
                            </div>
                            <p className="muted purchase-fulfillment">
                                {review.grade}/5
                                {" · "}
                                {review.authorName || review.authorUsername}
                                {review.anonymous ? " (anonyme en public)" : ""}
                                {" · "}
                                {review.status === "DELETED" ? "Masqué" : "Actif"}
                            </p>
                            {review.comment && (
                                <p className="purchase-fulfillment">{review.comment}</p>
                            )}
                            <p className="muted purchase-row-date">{formatReviewDate(review.createdAt)}</p>
                        </div>
                        {review.status === "ACTIVE" && (
                            <RowActionsMenu
                                ariaLabel={`Actions pour l'avis ${review.id}`}
                                items={[
                                    {
                                        id: "delete",
                                        label: "Masquer l'avis",
                                        danger: true,
                                        onClick: () => setPendingDelete(review)
                                    }
                                ]}
                            />
                        )}
                        {review.status === "DELETED" && (
                            <RowActionsMenu
                                ariaLabel={`Actions pour l'avis ${review.id}`}
                                items={[
                                    {
                                        id: "restore",
                                        label: "Rendre visible",
                                        onClick: () => setPendingRestore(review)
                                    }
                                ]}
                            />
                        )}
                    </div>
                ))}
            </div>

            <ConfirmDialog
                open={!!pendingDelete}
                title="Masquer l'avis"
                message="L'avis ne sera plus visible sur la fiche produit, mais restera en base pour l'historique."
                confirmLabel="Masquer"
                dangerous
                onConfirm={handleDelete}
                onCancel={() => setPendingDelete(null)}
            />
            <ConfirmDialog
                open={!!pendingRestore}
                title="Rendre l'avis visible"
                message="L'avis sera de nouveau affiché sur la fiche produit et pris en compte dans la moyenne."
                confirmLabel="Rendre visible"
                onConfirm={handleRestore}
                onCancel={() => setPendingRestore(null)}
            />
        </section>
    );
}
